import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  Icon,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import orderProgressIcon from "../../assets/order-progress-icon.png";
import { FilterValue } from "../../models/Filters/FilterValue";
import { OrderType } from "../../models/Project/OrderType";
import { ShipmentModes } from "../../models/ProjectManagement/ShipmentModes";
import { ShippingType } from "../../models/ProjectManagement/ShippingType";
import { PaymentMode } from "../../models/ProjectManagement/PaymentMode";
import { ProjectPaymentStatus } from "../../models/ProjectManagement/ProjectPaymentStatus";
import { Admin } from "../../models/TaskTracker/Admin";
import { useStores } from "../../use-stores";
import AppDropDown from "../../views/components/common/filter/AppDropDown";
import { observer } from "mobx-react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../utils/NotificationsContext";
import dayjs, { Dayjs } from "dayjs";
import DatePicker2 from "../../views/components/common/textFields/DatePicker2";
import AppButton from "../../views/components/common/buttons/AppButton";
import CustomTypography from "../../views/components/common/form/CustomTypography";
import CustomerHeading from "../../views/components/projects/projectDetails/CustomerHeading";
import SendNpsPopup from "../../views/components/projects/projectDetails/SendNpsPopup";
import { orderStatusOptions } from "../../models/Order/OrderDetails";
import RichTextEditor from "../../views/components/common/textEditor/RichTextEditor";
import AppSingleSearchDropdown from "../../views/components/common/filter/AppSingleSearchDropdown";
import PermissionBlockLabel from "../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../models/Employees/EmployeeRoles";
import CustomerDetailsCard from "../../views/components/projects/projectDetails/CustomerDetailsCard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationPopup from "../../views/components/common/pop-up/ConfirmationPopup";
import { formatDate } from "../../views/components/common/functions/formatDate";
import { OrderTypeEnum } from "../../models/ProjectManagement/ProjectManagementBasicDetails";
import { orderTypeFieldsMapping } from "./projects.types";

const ProjectDetails = () => {
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const projectId = Number(params["project-id"]);
  const {
    orderStore,
    projectManagementStore,
    TaskTrackerStore,
    userStore,
    warehouseStore,
  } = useStores();
  const { showError, showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendNpsPopup, setSendNpsPopup] = useState<boolean>(false);
  const [excludeFromProjectPopup, setExcludeFromProjectPopup] =
    useState<boolean>(false);
  const [excludeFromSourcingPopup, setExcludeFromSourcingPopup] =
    useState<boolean>(false);

  useEffect(() => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore.searchWarehouse().catch((err) => {
        console.error(err);
      });
    }

    if (projectManagementStore.currentProjectDetails?.order_id !== projectId) {
      setIsLoading(true);
      orderStore.getOrderBasicDetails(projectId).catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed to fetch comment, please try again later",
        );
      });
      projectManagementStore
        .getProjectDetails(projectId)
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch comment, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let apiTimer: any = 0;

  const handleAdminSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
          showError("Error", "Error occured while fetching admin");
        });
      }, 1000);
  };

  const handleSave = () => {
    const dispatchDate = projectManagementStore.currentProjectDetails
      ?.dispatch_date
      ? dayjs(projectManagementStore.currentProjectDetails.dispatch_date)
      : null;
    const deliveryDate = projectManagementStore.currentProjectDetails
      ?.delivery_date
      ? dayjs(projectManagementStore.currentProjectDetails.delivery_date)
      : null;

    if (dispatchDate && deliveryDate && dispatchDate.isAfter(deliveryDate)) {
      showError(
        "Invalid Date Range",
        "Dispatch date cannot be greater than delivery date. Please correct the dates before saving.",
      );
      return;
    }

    setIsLoading(true);
    projectManagementStore
      .saveProjectDetails()
      .catch((err) => {
        projectManagementStore.resetProjectDetails();
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendNps = (name: string, email: string) => {
    setIsLoading(true);
    projectManagementStore
      .sendNpsEmail(name, email, projectId)
      .then((res) => {
        if (res.success) {
          showSuccess("Email sent successfully");
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendCsat = () => {
    setIsLoading(true);
    projectManagementStore
      .sendCsatEmail(projectId)
      .then((res) => {
        if (res.success) {
          showSuccess("Message sent successfully");
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleStatusUpdate = (status: string, projectId: number) => {
    setIsLoading(true);
    orderStore
      .updateStatus(Number(status), projectId)
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const currentOrderTypeId =
    projectManagementStore.currentProjectDetails
      ?.order_type_id as OrderTypeEnum | undefined;
  const mappedFields =
    currentOrderTypeId !== undefined
      ? orderTypeFieldsMapping[currentOrderTypeId]
      : undefined;
  const visibleFields = new Set(
    (mappedFields ?? []).map((field) => field.toLowerCase()),
  );
  const shouldShowField = (fieldName: string) =>
    mappedFields === undefined || visibleFields.has(fieldName.toLowerCase());

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                color: theme.palette.customColors.greyTextColor,
              },
              "&:active": {
                color: theme.palette.customColors.greyTextColor,
              },
              mb: 1,
              maxWidth: "60px",
              // pl: { xl: 3, lg: 3, md: 0, sm: 0, xs: 0 },
            }}
            onClick={() => {
              navigate(`/project-management/listing`);
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "16px" }} />
            <span style={{ fontSize: "14px", fontWeight: "600" }}>Back</span>
          </Box>

          <Box
            sx={{
              display: "block",
              "@media (min-width:1285px)": {
                display: "flex",
              },
              justifyContent: "space-between",
              gap: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                "@media (min-width:1200px)": {
                  width: "40%",
                },
              }}
            >
              <CustomerHeading
                brandName={
                  projectManagementStore.currentProjectDetails.customer_name ||
                  ""
                }
                label={orderStore.orderBasicDetails?.label || ""}
                moduleName="Project Details"
                orderId={projectId}
                progressValue={
                  projectManagementStore.currentProjectDetails?.progress
                }
              />
            </Box>

            <Box
              sx={{
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {userStore.hasPermission(EmployeeRoles.CREATE_ORDER) &&
                userStore.hasPermission(EmployeeRoles.EDIT_ORDER) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "center",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Icon
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img src={orderProgressIcon} alt="" />
                      </Icon>
                      <CustomTypography variant="body1">
                        Order status:
                      </CustomTypography>
                    </Box>
                    <Box
                      sx={{
                        maxWidth: "127px",
                        minWidth: "127px",
                      }}
                    >
                      <AppDropDown
                        title=""
                        isMultiSelect={false}
                        fontSize="13px"
                        borderColor={theme.palette.customColors.purple}
                        list={orderStatusOptions}
                        value={String(
                          orderStore.orderBasicDetails?.status || "0",
                        )}
                        onSingleSelectionChange={(value) =>
                          handleStatusUpdate(value, projectId)
                        }
                        borderRadius="10px"
                        height="40px"
                        backgroundColor={theme.palette.customColors.lightPurple}
                        listPadding={"2px"}
                        disabled={
                          projectManagementStore.currentProjectDetails
                            .archived === 1
                        }
                      />
                    </Box>
                  </Box>
                )}
            </Box>
          </Box>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Box>
            {projectManagementStore.currentProjectDetails.auto_approved ===
            1 ? (
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.greyTextColor}
              >
                Auto Approved
                <span
                  style={{
                    fontWeight: 600,
                    color: theme?.palette?.customColors?.blackBorder,
                  }}
                >
                  {" "}
                  on{" "}
                  {formatDate(
                    projectManagementStore.currentProjectDetails.created_date ||
                      "",
                  )}
                </span>
              </CustomTypography>
            ) : (
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.greyTextColor}
              >
                Approved By:{" "}
                {projectManagementStore.currentProjectDetails.creator_name ||
                  "--"}
                <span
                  style={{
                    fontWeight: 600,
                    color: theme?.palette?.customColors?.blackBorder,
                  }}
                >
                  on{" "}
                  {formatDate(
                    projectManagementStore.currentProjectDetails.created_date ||
                      "",
                  )}
                </span>
              </CustomTypography>
            )}
          </Box>
          <Box
            sx={{
              display: { md: "block", lg: "flex" },
              justifyContent: { lg: "space-between", md: "none" },
              gap: { xl: 12, lg: 10 },
              flexWrap: "wrap",
            }}
            mt={4}
          >
            {/* Left Side*/}
            <Box sx={{ flex: 0.4, minWidth: "60%" }}>
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 20 }}
              >
                Project Management
              </CustomTypography>
              <Grid2 container spacing={5}>
                {shouldShowField("PM POC") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      PM POC
                    </CustomTypography>
                    <AppSingleSearchDropdown
                      key={"admin-pm"}
                      fontSize="14px"
                      showBorder={true}
                      inputHeight="40px"
                      list={
                        TaskTrackerStore?.admins?.map((admin: Admin) => ({
                          id: admin.id.toString(),
                          value: admin.name,
                        })) || []
                      }
                      value={
                        projectManagementStore.currentProjectDetails.pm_admin_id
                          ? {
                              id: String(
                                projectManagementStore.currentProjectDetails
                                  .pm_admin_id,
                              ),
                              value:
                                projectManagementStore.currentProjectDetails
                                  .pm_admin_name!,
                            }
                          : null
                      }
                      handleSearch={handleAdminSearch}
                      handleSelect={(selectedItem: FilterValue | null) => {
                        projectManagementStore.updateCurrentProjectDetails(
                          "pm_admin_id",
                          selectedItem ? Number(selectedItem.id) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "pm_admin_name",
                          selectedItem?.value || undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Sales POC") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Sales POC
                    </CustomTypography>
                    <AppSingleSearchDropdown
                      key={"sales-pm"}
                      fontSize="14px"
                      showBorder={true}
                      inputHeight="40px"
                      list={
                        TaskTrackerStore?.admins?.map((admin: Admin) => ({
                          id: admin.id.toString(),
                          value: admin.name,
                        })) || []
                      }
                      value={
                        projectManagementStore.currentProjectDetails
                          .sales_admin_id
                          ? {
                              id: String(
                                projectManagementStore.currentProjectDetails
                                  .sales_admin_id,
                              ),
                              value:
                                projectManagementStore.currentProjectDetails
                                  .sales_admin_name!,
                            }
                          : null
                      }
                      handleSearch={handleAdminSearch}
                      handleSelect={(selectedItem: FilterValue | null) => {
                        projectManagementStore.updateCurrentProjectDetails(
                          "sales_admin_id",
                          selectedItem ? Number(selectedItem.id) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "sales_admin_name",
                          selectedItem?.value || undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Payment Status") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Payment Status
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={ProjectPaymentStatus || []}
                      value={
                        projectManagementStore.currentProjectDetails &&
                        projectManagementStore.currentProjectDetails
                          .payment_status_id
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .payment_status_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_status_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_status_label",
                          selected
                            ? ProjectPaymentStatus.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Type Of Order") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Type Of Order
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={OrderType || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.order_type_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .order_type_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "order_type_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "order_type_label",
                          selected
                            ? OrderType.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Warehouse") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Warehouse
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={warehouseStore.warehouses.map((warehouse) => ({
                        id: warehouse.id,
                        value: warehouse.identifier,
                      }))}
                      value={
                        projectManagementStore.currentProjectDetails
                          .warehouse_id
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(id: string, name: string) => {
                        projectManagementStore.updateCurrentProjectDetails(
                          "warehouse_id",
                          id,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "warehouse_identifier",
                          name,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
              </Grid2>

              <Divider sx={{ my: 4 }} />
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 20 }}
              >
                Delivery & Shipment
              </CustomTypography>
              <Grid2 container spacing={5}>
                {shouldShowField("Dispatch Date") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Dispatch Date
                    </CustomTypography>

                    <DatePicker2
                      label=""
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.dispatch_date
                          ? dayjs(
                              projectManagementStore.currentProjectDetails
                                .dispatch_date,
                            )
                          : null
                      }
                      onChange={(date: Dayjs | null) => {
                        if (!date) return;

                        const formattedDate = date?.format(
                          "YYYY-MM-DD HH:mm:ss",
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "dispatch_date",
                          formattedDate,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Delivery Date") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Delivery Date
                    </CustomTypography>
                    <DatePicker2
                      label=""
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.delivery_date
                          ? dayjs(
                              projectManagementStore.currentProjectDetails
                                .delivery_date,
                            )
                          : null
                      }
                      onChange={(date: Dayjs | null) => {
                        if (!date) return;

                        const formattedDate = date?.format(
                          "YYYY-MM-DD HH:mm:ss",
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "delivery_date",
                          formattedDate,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Mode Of Shipment") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Mode of Shipment
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={ShipmentModes || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.shipment_mode_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .shipment_mode_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipment_mode_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipment_mode_label",
                          selected
                            ? ShipmentModes.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Type Of Shipment") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Type Of Shipment
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={ShippingType || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.shipping_type_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .shipping_type_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipping_type_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipping_type_label",
                          selected
                            ? ShippingType.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Flexible Delivery") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Flexible Delivery
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={[
                        { id: "-1", value: "" },
                        { id: "0", value: "No" },
                        { id: "1", value: "Yes" },
                      ]}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.is_delivery_date_flexible === undefined
                          ? "-1"
                          : projectManagementStore.currentProjectDetails
                                .is_delivery_date_flexible
                            ? "1"
                            : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      disabled={true}
                    />
                  </Box>
                  </Grid2>
                )}
                {shouldShowField("Mode Of Payment") && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Mode Of Payment
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={PaymentMode || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.payment_mode_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .payment_mode_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_mode_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_mode_label",
                          selected
                            ? PaymentMode.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                  </Grid2>
                )}
              </Grid2>

              {shouldShowField("Project Notes") && (
                <>
                  <Divider sx={{ my: 4 }} />
                  <CustomTypography
                    variant="heading5"
                    style={{ fontWeight: 600, marginBottom: 20 }}
                  >
                    Project Notes
                  </CustomTypography>
                  <Grid2 container spacing={5}>
                    <Grid2 size={{ xs: 12 }}>
                      <Box>
                        <RichTextEditor
                          value={
                            projectManagementStore.currentProjectDetails
                              .order_additional_info || ""
                          }
                          onChange={(value: string) =>
                            projectManagementStore.updateProjectNotes(value)
                          }
                          maxLength={1000}
                          placeholder={
                            projectManagementStore.currentProjectDetails
                              .archived === 1
                              ? ""
                              : "You can add any additional information here. Eg. Product wise initial information, packing instructions, link to the drive with logos and creatives, etc."
                          }
                          disabled={
                            projectManagementStore.currentProjectDetails
                              .archived === 1
                          }
                        />
                      </Box>
                    </Grid2>
                  </Grid2>
                </>
              )}

              {orderStore.orderBasicDetails?.services &&
                orderStore.orderBasicDetails?.services?.length > 0 && (
                  <>
                    <Divider sx={{ my: 4 }} />
                    <CustomTypography
                      variant="heading5"
                      style={{ fontWeight: 600, marginBottom: 20 }}
                    >
                      Added Services
                    </CustomTypography>
                    <Grid2 container spacing={5}>
                      <Grid2 size={{ xs: 12 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            mt: 2,
                            border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
                            borderRadius: "4px",
                            padding: 2,
                          }}
                        >
                          {orderStore.orderBasicDetails?.services.map(
                            (service) => {
                              const icon = service.vector_image_url;
                              return (
                                <Chip
                                  key={service.id}
                                  label={
                                    service.name + " " + service.custom_name
                                  }
                                  avatar={
                                    icon ? (
                                      <Icon style={{ fontSize: "15px" }}>
                                        <img
                                          src={icon}
                                          alt={service.custom_name || ""}
                                        />
                                      </Icon>
                                    ) : undefined
                                  }
                                  sx={{
                                    backgroundColor:
                                      theme.palette.customColors.greyBackground,
                                    fontWeight: 600,
                                    borderRadius: "4px",
                                    height: "32px",
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                  }}
                                />
                              );
                            },
                          )}
                        </Box>
                      </Grid2>
                    </Grid2>
                  </>
                )}

              <Grid2 container spacing={5} sx={{ mt: 2 }}>
                <Grid2 size={{ xs: 12 }}>
                  {userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) &&
                    userStore.hasPermission(EmployeeRoles.EDIT_PROJECT) && (
                      <AppButton
                        variant="primary"
                        label="Save"
                        customStyles={{
                          width: "120px",
                        }}
                        onClick={() => handleSave()}
                        disabled={
                          projectManagementStore.currentProjectDetails
                            .archived === 1
                        }
                      />
                    )}
                </Grid2>
              </Grid2>
            </Box>

            {/* Right Side*/}
            <Box
              mt={{ sm: 2, xs: 2, mt: 2 }}
              sx={{
                flex: 0.3,
                minWidth: "30%",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <CustomerDetailsCard />
              {/* <Card
                sx={{
                  padding: { xl: 4, lg: 3, md: 3, sm: 2, xs: 2 },
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: `1px solid ${theme.palette.customColors.greyBorder}`,
                }}
              >
                <Grid2 size={{ xs: 12 }}>
                  <CustomTypography
                    variant="heading5"
                    style={{ fontWeight: 600, marginBottom: 20 }}
                  >
                    Exclude From Analytics
                  </CustomTypography>
                  <Box sx={{ margin: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={
                            Boolean(
                              projectManagementStore.currentProjectDetails
                                .excluded_from_projects,
                            ) === true
                          }
                          onChange={(_, checked) => {
                            if (checked) setExcludeFromProjectPopup(checked);
                            else
                              projectManagementStore.updateExcludeFromProjects(
                                false,
                                null,
                              );
                          }}
                          disabled={
                            projectManagementStore.currentProjectDetails
                              .archived === 1
                          }
                        />
                      }
                      label={
                        <CustomTypography
                          variant="heading8"
                          color={theme.palette.customColors.blackBorder}
                        >
                          Projects
                        </CustomTypography>
                      }
                      sx={{ fontSize: "0.5rem", marginBottom: 0.8 }}
                    />
                    {Boolean(
                      projectManagementStore.currentProjectDetails
                        .excluded_from_projects,
                    ) &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_projects &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_projects.length > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color={theme.palette.customColors.greyTextColor}
                            style={{ marginBottom: 0.8 }}
                          >
                            Reason:{" "}
                            {
                              projectManagementStore.currentProjectDetails
                                .reason_to_exclude_from_projects
                            }
                          </CustomTypography>
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_projects_by_name && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                              style={{ marginBottom: 0.8 }}
                            >
                              Admin:{" "}
                              {
                                projectManagementStore.currentProjectDetails
                                  .excluded_from_projects_by_name
                              }
                            </CustomTypography>
                          )}
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_project_date && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                            >
                              Date:{" "}
                              {projectManagementStore.currentProjectDetails
                                .excluded_from_project_date
                                ? new Date(
                                    projectManagementStore.currentProjectDetails
                                      .excluded_from_project_date,
                                  ).toLocaleString()
                                : ""}
                            </CustomTypography>
                          )}
                        </Box>
                      )}
                  </Box>
                  <Box sx={{ margin: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={
                            Boolean(
                              projectManagementStore.currentProjectDetails
                                .excluded_from_sourcing,
                            ) === true
                          }
                          onChange={(_, checked) => {
                            if (checked) setExcludeFromSourcingPopup(checked);
                            else
                              projectManagementStore.updateExcludeFromSourcing(
                                false,
                                null,
                              );
                          }}
                          disabled={
                            projectManagementStore.currentProjectDetails
                              .archived === 1
                          }
                        />
                      }
                      label={
                        <CustomTypography
                          variant="heading7"
                          color={theme.palette.customColors.blackBorder}
                        >
                          Sourcing
                        </CustomTypography>
                      }
                      sx={{ fontSize: "0.5rem", marginBottom: 0.8 }}
                    />
                    {Boolean(
                      projectManagementStore.currentProjectDetails
                        .excluded_from_sourcing,
                    ) &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_sourcing &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_sourcing.length > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color={theme.palette.customColors.greyTextColor}
                            style={{ marginBottom: 0.8 }}
                          >
                            Reason:{" "}
                            {
                              projectManagementStore.currentProjectDetails
                                .reason_to_exclude_from_sourcing
                            }
                          </CustomTypography>
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_sourcing_by_name && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                              style={{ marginBottom: 0.8 }}
                            >
                              Admin:{" "}
                              {
                                projectManagementStore.currentProjectDetails
                                  .excluded_from_sourcing_by_name
                              }
                            </CustomTypography>
                          )}
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_sourcing_date && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                            >
                              Date:{" "}
                              {projectManagementStore.currentProjectDetails
                                .excluded_from_sourcing_date
                                ? new Date(
                                    projectManagementStore.currentProjectDetails
                                      .excluded_from_sourcing_date,
                                  ).toLocaleString()
                                : ""}
                            </CustomTypography>
                          )}
                        </Box>
                      )}
                  </Box>
                </Grid2>
              </Card> */}
            </Box>
          </Box>
          <Box>
            <SendNpsPopup
              open={sendNpsPopup}
              onClose={() => setSendNpsPopup(!sendNpsPopup)}
              onSubmit={(name: string, email: string) => {
                handleSendNps(name, email);
                setSendNpsPopup(!sendNpsPopup);
              }}
            />
          </Box>
          <ConfirmationPopup
            id={projectManagementStore.currentProjectDetails.order_id}
            title={"Reason"}
            content={`You are going to exclude project ${projectManagementStore.currentProjectDetails.order_id} from projects analytics. State your reason here?`}
            type="form"
            inputRequired={true}
            inputLabel="Reason"
            inputType="text"
            onCancel={() => {
              setExcludeFromProjectPopup(false);
              projectManagementStore.updateExcludeFromProjects(false, null);
            }}
            onConfirm={(id, input) => {
              if (input) {
                setExcludeFromProjectPopup(false);
                projectManagementStore.updateExcludeFromProjects(true, input);
              }
            }}
            open={excludeFromProjectPopup}
          />
          <ConfirmationPopup
            id={projectManagementStore.currentProjectDetails.order_id}
            title={"Reason"}
            content={`You are going to exclude project ${projectManagementStore.currentProjectDetails.order_id} from sourcing analytics. State your reason here?`}
            type="form"
            inputRequired={true}
            inputLabel="Reason"
            inputType="text"
            onCancel={() => {
              projectManagementStore.updateExcludeFromSourcing(false, null);
              setExcludeFromSourcingPopup(false);
            }}
            onConfirm={(id, input) => {
              if (input) {
                setExcludeFromSourcingPopup(false);
                projectManagementStore.updateExcludeFromSourcing(true, input);
              }
            }}
            open={excludeFromSourcingPopup}
          />
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </Grid2>
  );
};

export default observer(ProjectDetails);
